import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import CommonButton from '../../Components/CommonButton';
import CommonDropdown from '../../Components/CommonDropdown';
import CommonSearchBar from '../../Components/CommonSearchBar';
import CommonPagination from '../../Components/CommonPagination';
import CommonTable from '../../Components/CommonTable';
import ButtonLoader from '../../Components/ButtonLoader';
import StatusBadge from '../../Components/StatusBadge';
import AddVendorModal from '../components/AddVendorModal';
import InviteVendorModal from '../components/InviteVendorModal';
import VendorProfileModal from '../components/VendorProfileModal';
import ScanCadenceModal from '../components/ScanCadenceModal';
import { useConfirm } from '../../Components/ConfirmModal';
import { bulk_upload_vendors, delete_by_id_thirdparty_domain, download_vendor_template, third_party_download, third_party_monitored_vendors } from '../../services';
import { criticalityTone, formatDate, gradeTone, MISSING_TEXT, safeArray, vendorGradeFallback } from '../tpcrmUtils';
import '../../styles/TpcrmPortal.scss';

const PER_PAGE = 10;

const CRITICALITY_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

const GRADE_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'F', label: 'F' },
];

// Single "More" trigger (a real primary CommonButton, not CommonDropdown's
// value-select look) replacing the old side-by-side Invite/Delete buttons —
// opens a small menu with Invite/Delete/View Details on click. Closes on
// outside click, same pattern CommonDropdown itself already uses.
function RowActionsMenu({ onInvite, onDelete, onViewDetails }) {
    const [open, setOpen] = useState(false);
    // The menu is rendered through a portal with position:fixed so it escapes
    // CommonTable's horizontally-scrolling wrapper — a scroll box clips BOTH
    // axes (CSS spec), so an in-table absolute popover gets cut off and no
    // overflow tweak on the table can free it. `pos` is the viewport rect
    // computed from the trigger when the menu opens.
    const [pos, setPos] = useState(null);
    const boxRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = () => setOpen(false);
        const onDocClick = (e) => {
            if (
                boxRef.current && !boxRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        // A fixed menu doesn't follow its anchor, so dismiss it on scroll/resize.
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    const handleToggle = () => {
        setOpen((o) => {
            if (!o && boxRef.current) {
                const rect = boxRef.current.getBoundingClientRect();
                const MENU_H = 140;
                const flipUp = rect.bottom + MENU_H > window.innerHeight;
                setPos({
                    flipUp,
                    top: rect.bottom + 4,
                    bottom: window.innerHeight - rect.top + 4,
                    right: window.innerWidth - rect.right,
                });
            }
            return !o;
        });
    };

    const runAndClose = (fn) => () => { setOpen(false); fn(); };

    return (
        <div ref={boxRef} className="tpcrm-row-menu">
            <CommonButton
                type="button"
                variant="primary"
                text="More"
                className="tpcrm-row-btn"
                onClick={handleToggle}
            />
            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    className="tpcrm-row-menu-popover"
                    role="menu"
                    style={{
                        position: 'fixed',
                        top: pos.flipUp ? 'auto' : pos.top,
                        bottom: pos.flipUp ? pos.bottom : 'auto',
                        right: pos.right,
                        zIndex: 1100,
                    }}
                >
                    <button type="button" role="menuitem" className="tpcrm-row-menu-item" onClick={runAndClose(onInvite)}>Invite</button>
                    <button type="button" role="menuitem" className="tpcrm-row-menu-item tpcrm-row-menu-item--danger" onClick={runAndClose(onDelete)}>Delete</button>
                    <button type="button" role="menuitem" className="tpcrm-row-menu-item" onClick={runAndClose(onViewDetails)}>View Details</button>
                </div>,
                document.body
            )}
        </div>
    );
}

function TpcrmVendorsView() {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [criticality, setCriticality] = useState('');
    const [grade, setGrade] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [gradeSort, setGradeSort] = useState('');
    const [showAddVendor, setShowAddVendor] = useState(false);
    const [downloadBusy, setDownloadBusy] = useState(false);
    const [templateBusy, setTemplateBusy] = useState(false);
    const [bulkUploading, setBulkUploading] = useState(false);
    const [inviteVendorModal, setInviteVendorModal] = useState(false);
    const [vendorRelationId, setVendorRelationId] = useState(null);
    const [profileVendor, setProfileVendor] = useState(null);
    // Cadence lives here rather than on its own route: this is the page with
    // the Next Scan column, which is what cadence produces.
    const [showCadence, setShowCadence] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const location = useLocation();
    const bulkUploadRef = useRef(null);
    const { confirm, confirmModal } = useConfirm();

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3500);
    };

    const handleDeleteVendor = async (vendor) => {
        const vendorName = vendor?.vendor_name || vendor?.target_organization_name || 'this vendor';
        if (!(await confirm({
            title: 'Delete vendor',
            message: `Delete "${vendorName}"? This cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            themeColor: false,
        }))) return;
        try {
            const response = await delete_by_id_thirdparty_domain({ id: vendor?.target_organization_id });
            const message = response?.statusCode?.response_data?.message;
            if (message) {
                fetchVendors();
                showToast('Vendor deleted successfully.', 'success');
            } else {
                showToast('Unable to delete this vendor.', 'error');
            }
        } catch (error) {
            console.error('Failed to delete vendor:', error);
            showToast('Unable to delete this vendor.', 'error');
        }
    };

    const fetchVendors = useCallback(async () => {
        setLoading(true);
        try {
            const response = await third_party_monitored_vendors({ search, ordering: gradeSort || undefined });
            const data = response?.statusCode?.response_data?.data || [];
            setVendors(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching TPCRM vendors:', error);
            setVendors([]);
        } finally {
            setLoading(false);
        }
    }, [search, gradeSort]);

    useEffect(() => {
        fetchVendors();
    }, [fetchVendors]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const filter = params.get('filter');
        const queryCriticality = params.get('criticality');
        const queryGrade = params.get('grade');

        if (queryCriticality) {
            setCriticality(queryCriticality.toLowerCase());
        } else if (filter === 'critical') {
            setCriticality('critical');
        } else if (!queryCriticality) {
            setCriticality('');
        }

        if (queryGrade) {
            setGrade(queryGrade.toUpperCase());
        } else if (filter === 'incidents') {
            setGrade('F');
        } else if (!queryGrade) {
            setGrade('');
        }
    }, [location.search]);

    const exportCsv = async () => {
        setDownloadBusy(true);
        try {
            const response = await third_party_download({ params: {} });
            const blob = new Blob([response.statusCode], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'tpcrm-vendors.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Vendor export failed:', error);
            showToast('Unable to export vendor list.', 'error');
        } finally {
            setDownloadBusy(false);
        }
    };

    const downloadTemplate = async () => {
        setTemplateBusy(true);
        try {
            const response = await download_vendor_template();
            const blob = new Blob([response.statusCode], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'tpcrm-vendor-template.xlsx';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Template download failed:', error);
            showToast('Unable to download the template.', 'error');
        } finally {
            setTemplateBusy(false);
        }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setBulkUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await bulk_upload_vendors(formData);
            const data = response?.data || response?.statusCode || response;
            if (data?.status === 1 || data?.status === 'success') {
                const responseData = data?.response_data;
                const summary = responseData?.message || 'Upload completed.';
                const failed = responseData?.data?.failed || [];
                let details = summary;
                if (failed.length) {
                    details += '\n\nFailed rows:';
                    failed.forEach((f) => {
                        details += `\nRow ${f.row}: ${(f.errors || []).join(', ')}`;
                    });
                }
                showToast(details, failed.length ? 'warning' : 'success');
                fetchVendors();
            } else {
                showToast(data?.error?.message || data?.message || 'Upload failed.', 'error');
            }
        } catch (error) {
            console.error('Bulk upload failed:', error);
            const errMsg = error?.response?.data?.error?.message || 'Upload failed. Please check the file and try again.';
            showToast(errMsg, 'error');
        } finally {
            setBulkUploading(false);
            e.target.value = '';
        }
    };

    const filteredVendors = useMemo(() => {
        return safeArray(vendors).filter((vendor) => {
            const vendorGrade = vendorGradeFallback(vendor);
            const vendorCriticality = String(vendor?.business_criticality || '').toLowerCase();
            const matchesCriticality = !criticality || vendorCriticality === criticality;
            const matchesGrade = !grade || vendorGrade === grade;
            return matchesCriticality && matchesGrade;
        });
    }, [vendors, criticality, grade]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search, criticality, grade]);

    const pageCount = Math.max(1, Math.ceil(filteredVendors.length / PER_PAGE));
    const pagedVendors = filteredVendors.slice(currentPage * PER_PAGE, currentPage * PER_PAGE + PER_PAGE);
    const handlePageClick = ({ selected }) => setCurrentPage(selected);

    // Restores MonitoredVendors.js's server-side grade sort (toggles
    // overall_grade/-overall_grade via third_party_monitored_vendors'
    // `ordering` param) — was replaced by the Grade filter dropdown alone
    // during the rebuild; the filter narrows to one grade, this actually
    // orders the whole list, so both are kept side by side.
    const handleGradeSort = () => {
        setGradeSort((prev) => (prev === 'overall_grade' ? '-overall_grade' : 'overall_grade'));
    };

    const headers = [
        'Vendor', 'Service', 'Criticality',
        <span key="grade" className="tpcrm-sortable-th" onClick={handleGradeSort}>Current Grade ↕</span>,
        'Next Scan', 'Last Scan', 'Actions',
    ];

    // An empty cell now names what is absent, in the column's own terms.
    // `formatDate` returns '-' for a missing/unparseable date, which is the
    // signal that there is nothing to show.
    const cellDate = (raw, missing) => {
        const formatted = formatDate(raw);
        return formatted === '-'
            ? <span className="tpcrm-cell-empty">{missing}</span>
            : <span className="tpcrm-cell-nowrap">{formatted}</span>;
    };

    const rows = pagedVendors.map((vendor) => {
        const vendorGrade = vendorGradeFallback(vendor);
        const criticalLabel = vendor?.business_criticality;
        const vendorName = vendor?.vendor_name || vendor?.target_organization_name;
        const service = vendor?.service_provided;
        return {
            data: [
                <div
                    className="tpcrm-vendor-name tpcrm-vendor-name--clickable tpcrm-cell-truncate"
                    title={vendorName ? `${vendorName} — view profile` : 'View profile'}
                    onClick={() => setProfileVendor(vendor)}
                >
                    {vendorName || MISSING_TEXT.vendor}
                </div>,
                service
                    ? <div className="tpcrm-cell-truncate" title={service}>{service}</div>
                    : <span className="tpcrm-cell-empty">{MISSING_TEXT.service}</span>,
                criticalLabel
                    ? <StatusBadge dot={false} style={criticalityTone(criticalLabel)}>{criticalLabel}</StatusBadge>
                    : <span className="tpcrm-cell-empty">{MISSING_TEXT.criticality}</span>,
                vendorGrade === '-'
                    ? <span className="tpcrm-cell-empty">{MISSING_TEXT.grade}</span>
                    : <span className="tpcrm-grade-text" style={{ color: gradeTone(vendorGrade) }}>{vendorGrade}</span>,
                cellDate(vendor?.next_assessment_scheduled_date, MISSING_TEXT.nextScan),
                // Adding a vendor domain auto-runs the first scan, so the
                // relation's own `created_at` is the last-scan date — it's
                // present for every scanned vendor, including those that have
                // never been sent an assessment (where `latest_assessment` is
                // null). Date only, no time — Next Scan and Last Scan sit side
                // by side, and a bare date next to a date+time made the two
                // columns read as different kinds of value; the scan time isn't
                // actionable.
                cellDate(vendor?.created_at, MISSING_TEXT.lastScan),
                <RowActionsMenu
                    onInvite={() => {
                        setVendorRelationId(vendor?.id || vendor?.relation_id);
                        setInviteVendorModal(true);
                    }}
                    onDelete={() => handleDeleteVendor(vendor)}
                    onViewDetails={() => setProfileVendor(vendor)}
                />,
            ],
        };
    });

    return (
        <div className="tpcrm-page">
            <div className="tpcrm-card cmn_elevated_card">
                <div className="tpcrm-card-header">
                    <div>
                        <h3>Vendor Directory</h3>
                        <div className="tpcrm-card-subtitle">
                            {loading
                                ? 'Refreshing vendor data'
                                : filteredVendors.length
                                    ? `Showing ${currentPage * PER_PAGE + 1}–${Math.min(filteredVendors.length, currentPage * PER_PAGE + PER_PAGE)} of ${filteredVendors.length} vendors`
                                    : 'No vendors match the current filters'}
                        </div>
                    </div>

                    <div className="tpcrm-page-actions">
                        <CommonButton
                            type="button"
                            text="Scan Cadence"
                            className="tpcrm-action-btn"
                            onClick={() => setShowCadence(true)}
                        />
                        <CommonDropdown
                            ariaLabel="Add Vendor"
                            value={null}
                            placeholder="+ Add Vendor"
                            disabled={bulkUploading}
                            onChange={(action) => {
                                if (action === 'manual') setShowAddVendor(true);
                                if (action === 'bulk') bulkUploadRef.current?.click();
                            }}
                            options={[
                                { value: 'manual', label: 'Manual Create' },
                                { value: 'bulk', label: bulkUploading ? <ButtonLoader /> : 'Bulk Upload' },
                            ]}
                        />
                        <input
                            type="file"
                            ref={bulkUploadRef}
                            accept=".csv,.xlsx,.xls"
                            style={{ display: 'none' }}
                            onChange={handleBulkUpload}
                        />
                        <CommonDropdown
                            ariaLabel="Download"
                            value={null}
                            placeholder="Download"
                            disabled={downloadBusy || templateBusy}
                            onChange={(action) => {
                                if (action === 'export') exportCsv();
                                if (action === 'template') downloadTemplate();
                            }}
                            options={[
                                { value: 'export', label: downloadBusy ? 'Exporting...' : 'Export Vendors' },
                                { value: 'template', label: templateBusy ? 'Downloading...' : 'Download Template' },
                            ]}
                        />
                    </div>
                </div>

                <div className="tpcrm-page-actions tpcrm-filters-row">
                    <CommonSearchBar
                        placeholder="Search vendors"
                        value={search}
                        onChange={setSearch}
                        minWidth={200}
                        style={{ width: 220, maxWidth: 220, flex: '0 0 auto' }}
                    />
                    <CommonDropdown
                        ariaLabel="Criticality"
                        value={criticality}
                        onChange={setCriticality}
                        options={CRITICALITY_OPTIONS}
                        minWidth={140}
                    />
                    <CommonDropdown
                        ariaLabel="Grade"
                        value={grade}
                        onChange={setGrade}
                        options={GRADE_OPTIONS}
                        minWidth={110}
                    />
                </div>

                {loading ? (
                    <div className="tpcrm-loader"><ButtonLoader /></div>
                ) : (
                    <>
                        <CommonTable headers={headers} rows={rows} noPadding noScroll />

                        {pageCount > 1 && (
                            <CommonPagination handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />
                        )}
                    </>
                )}
            </div>

            {showAddVendor && (
                <AddVendorModal
                    show={showAddVendor}
                    onHide={() => setShowAddVendor(false)}
                    onSuccess={() => {
                        setShowAddVendor(false);
                        fetchVendors();
                        showToast('Vendor created successfully.', 'success');
                    }}
                />
            )}

            {inviteVendorModal && (
                <InviteVendorModal
                    show={inviteVendorModal}
                    onHide={() => setInviteVendorModal(false)}
                    id={vendorRelationId}
                    onSuccess={() => {
                        setInviteVendorModal(false);
                        fetchVendors();
                        showToast('Vendor invited successfully.', 'success');
                    }}
                />
            )}

            {profileVendor && (
                <VendorProfileModal
                    show={Boolean(profileVendor)}
                    vendor={profileVendor}
                    onHide={() => setProfileVendor(null)}
                    onUpdated={() => {
                        fetchVendors();
                        showToast('Vendor updated successfully.', 'success');
                    }}
                />
            )}

            {showCadence && (
                <ScanCadenceModal
                    show={showCadence}
                    onHide={() => setShowCadence(false)}
                    onSaved={fetchVendors}
                    // Already in state for the table — lets the modal show how
                    // many vendors each tier governs without another request.
                    vendors={vendors}
                />
            )}

            {confirmModal}

            {toast.show && (
                <div className={`custom-toast ${toast.type}`}>
                    <span className="message">{toast.message}</span>
                </div>
            )}
        </div>
    );
}

export default TpcrmVendorsView;
